import React from "react";
import useRouter from "use-react-router";
import { useTranslation } from "react-i18next";
import { Gig } from "gql-gen";
import { getAppUrl } from "utilities/routes";
import { Link } from "react-router-dom";

import { UserInfo, useUserInfo } from "modules/Dashboard/UserInfo";

import { isEditable } from "utilities/gigAccess";
import { TFunction } from "i18next";
import { Location } from "history";

import styles from "./styles.scss";

interface EditLinkProps {
  isEditing: boolean;
  userInfo: UserInfo;
  location: Location;
  t: TFunction;
  gigId: string;
}

const EditLink: React.FC<EditLinkProps> = ({ isEditing, userInfo, location, t, gigId }) => {
  const isAppOnly = userInfo?.me?.organizationUser.theAppReportsEnabled;
  const label = t(`gigs.cells.reportOverview.${isEditing ? "view" : "edit"}`);

  if (isAppOnly) {
    // Editing happens in TheApp, in this tab, and TheApp brings the user back:
    // `continueUrl` is where its report form lands the filler once they are
    // done, in place of the org home or the legacy report page. Without it,
    // editing a report from this modal ends somewhere else entirely, in a
    // second tab, with no way back to the row you started from.
    //
    // `window.location.href` rather than a rebuilt URL: the dashboard is
    // hash-routed in development and not in production, and this is the one
    // value that is right in both. `getAppUrl` percent-encodes it, which it
    // must — the href carries its own `?`, `&` and `=`.
    const editUrl = getAppUrl(
      "theApp",
      `${userInfo?.me?.organizationUser.organizationId}/reports/${gigId}`,
      { continueUrl: window.location.href },
    );

    return (
      <a className={styles.editButton} href={editUrl}>
        {label}
      </a>
    );
  }

  return (
    <Link
      className={styles.editButton}
      to={{
        ...location,
        pathname: isEditing
          ? location.pathname.split("/+edit-report")[0]
          : location.pathname + "/+edit-report",
      }}
    >
      {label}
    </Link>
  );
};

interface EditReportLinksProps {
  gig: Gig;
  className?: string;
}

export const EditReportLinks: React.FC<EditReportLinksProps> = ({ gig, className }) => {
  const userInfo = useUserInfo();
  const { t } = useTranslation();
  const { location } = useRouter();
  const isEditing = location.pathname.includes("/+edit-report");

  const { editable } = isEditable(gig, userInfo, 'dashboard');

  if (!editable) {
    return null;
  }

  return (
    <div className={className}>
      <EditLink isEditing={isEditing} userInfo={userInfo} location={location} t={t} gigId={gig.id} />
    </div>
  );
};
